import { useEffect, useState } from 'react';
import api from '../services/api';

export type AppConfig = {
  // Toggle auth téléphone mobile (table `setting` côté admin, clé SMS_ENABLED).
  // `mobile_auth_enabled` est le champ canonique ; `sms_enabled` est conservé
  // en alias pour les consommateurs existants.
  mobile_auth_enabled: boolean;
  sms_enabled: boolean;
};

const DEFAULT_CONFIG: AppConfig = {
  mobile_auth_enabled: false,
  sms_enabled: false,
};

let cached: AppConfig | null = null;
let inflight: Promise<AppConfig> | null = null;

/**
 * Charge /api/app-config une fois et le mémorise pour le reste de la session.
 * Utilisé par les pages d'inscription et de profil (#107) pour adapter le flow
 * selon la configuration runtime du serveur.
 */
export function useAppConfig(): { config: AppConfig; loading: boolean } {
  const [config, setConfig] = useState<AppConfig>(cached ?? DEFAULT_CONFIG);
  const [loading, setLoading] = useState<boolean>(cached === null);

  useEffect(() => {
    if (cached !== null) {
      return;
    }
    if (inflight === null) {
      inflight = api.get<Partial<AppConfig>>('/app-config')
        .then((res) => {
          const data = res.data || {};
          // Champ canonique = mobile_auth_enabled ; fallback sur l'alias sms_enabled.
          const mobileAuth = data.mobile_auth_enabled ?? data.sms_enabled ?? DEFAULT_CONFIG.mobile_auth_enabled;
          cached = {
            ...DEFAULT_CONFIG,
            mobile_auth_enabled: mobileAuth,
            sms_enabled: mobileAuth,
          };
          return cached;
        })
        .catch(() => {
          cached = DEFAULT_CONFIG;
          return cached;
        })
        .finally(() => { inflight = null; });
    }
    inflight.then((c) => {
      setConfig(c);
      setLoading(false);
    });
  }, []);

  return { config, loading };
}
